import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '../../lib/api.ts';

interface OrdersResponse {
  results: any[];
  count: number;
  total_count: number;
  status_counts: Record<string, number>;
}

interface OrdersParams {
  page?: string;
  page_size?: string;
  status?: string;
  search?: string;
  payment_method?: string;
  shipping_method?: string;
  date_from?: string;
  date_to?: string;
}

/**
 * Hook profesional para gestión de pedidos
 * SIN POLLING - Solo se actualiza cuando el WebSocket avisa
 */
export function usePedidos(params: OrdersParams = {}) {
  return useQuery<OrdersResponse>({
    queryKey: ['pedidos', params],
    queryFn: async () => {
      try {
        const { data } = await api.get('/api/orders', { params });
        return data;
      } catch (error) {
        console.error('Error fetching pedidos:', error);
        throw error;
      }
    },
    // 🚀 CONFIGURACIÓN PARA PUSH NOTIFICATIONS (SIN POLLING)
    staleTime: 300000,             // 5 minutos - cache largo porque WebSocket actualiza
    gcTime: 600000,                // 10 minutos en cache
    refetchOnWindowFocus: true,    // Solo al enfocar ventana
    refetchOnMount: true,          // Solo al montar
    refetchInterval: false,        // ❌ NO POLLING - WebSocket se encarga
    refetchIntervalInBackground: false, // ❌ NO polling en background
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });
}

export function usePedido(pedidoId: string | undefined) {
  return useQuery({
    queryKey: ['pedidos', pedidoId],
    enabled: Boolean(pedidoId),
    // 🚀 ACTUALIZACIÓN MÁS FRECUENTE PARA DETALLES
    refetchInterval: 2000,   // 2 segundos - respuesta rápida
    staleTime: 1000,          // 1 segundo - cache muy corto para cambios inmediatos
    queryFn: async () => {
      const { data } = await api.get(`/api/orders/${pedidoId}`);
      return data;
    },
  });
}

export function usePedidoActions() {
  const queryClient = useQueryClient();
  
  const makeAction = (endpoint: string, success: string) =>
    useMutation({
      mutationFn: async (pedidoId: string) => {
        const { data } = await api.post(`/api/orders/${pedidoId}/${endpoint}`);
        return data;
      },
      onSuccess: (data, pedidoId) => {
        toast.success(success);
        
        // 1. ⚡ ACTUALIZACIÓN INSTANTÁNEA del pedido individual
        queryClient.setQueryData(['pedidos', pedidoId], data);
        
        // 2. 🔄 Invalidar y refetch INMEDIATO de la lista
        queryClient.invalidateQueries({ queryKey: ['pedidos'] });
        queryClient.refetchQueries({ queryKey: ['pedidos'] });

        // 3. 🔥 ACTUALIZACIÓN DE REPORTES
        queryClient.invalidateQueries({ queryKey: ['report-summary'] });
        queryClient.invalidateQueries({ queryKey: ['report-sales'] });
        queryClient.invalidateQueries({ queryKey: ['report-orders'] });
        
        // 4. 📊 ACTUALIZAR DASHBOARD (Ventas del día)
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'summary'] });
        queryClient.refetchQueries({ queryKey: ['dashboard', 'summary'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard', 'pedidos'] });
        queryClient.refetchQueries({ queryKey: ['dashboard', 'pedidos'] });
        
        // 5. 🔔 ACTUALIZAR CONTADOR DE NOTIFICACIONES
        queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
        queryClient.refetchQueries({ queryKey: ['notifications', 'unread-count'] });
      },
      onError: () => toast.error('No se pudo actualizar el pedido'),
    });

  return {
    markPaid: makeAction('mark-paid', 'Pedido marcado como pagado'),
    markReady: makeAction('mark-ready-for-pickup', 'Pedido listo para retiro'),
    markOut: makeAction('mark-out-for-delivery', 'Pedido en camino'),
    markDelivered: makeAction('mark-delivered', 'Pedido entregado'),
    
    attachProof: useMutation({
      mutationFn: async ({ pedidoId, proof, email }: any) => {
        const formData = new FormData();
        formData.append('proof', proof);
        if (email) {
          formData.append('email', email);
        }
        const { data } = await api.post(`/api/orders/${pedidoId}/attach-proof`, formData);
        return data;
      },
      onSuccess: () => {
        toast.success('Comprobante enviado');
        queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      },
      onError: () => toast.error('No se pudo adjuntar el comprobante'),
    }),
  };
}